<template>
    <div class="mtMatchDisplay">
        <!-- match info html here -->
        <div class="match-info">
            <div v-if="!isInMatchDetail" class="match-title JARVIS_MatchCard_Img_Title">
                <span v-if="liveStatus.onLive" class="notranslate">
                    {{ getTranslationBySite('live').toUpperCase() }}
                </span>
                <span v-else>
                    <span v-if="liveStatus.tvLive" class="match-livetag notranslate">
                        {{ getTranslationBySite('live').toUpperCase() }}
                    </span>
                    <span v-if="liveStatus.isToday" class="match-todaytag">
                        {{ i18n.t('oddsDisplay_matchInfo_header_todayTag') }}
                    </span>
                </span>
                <span class="match-info-leagueName">
                    {{ leagueName }}
                </span>
                <span
                    class="match-title-refresh JARVIS_MatchCard_Btn_Refresh"
                    v-bind:class="{ _refreshing: isRefreshing }"
                    v-on:click="refresh(true)"
                >
                    <SvgIcon iconName="systemIcon_refresh_centered" />
                </span>
            </div>
            <div class="match-info-team">
                <div
                    class="match-info-team-name JARVIS_MatchCard_Img_TeamNameA"
                    v-bind:class="{
                        _favorite: match.isHomeFavorite,
                        _underdog: !match.isHomeFavorite,
                    }"
                >
                    {{ homeTeamName }}
                </div>
                <template v-if="match && !match.isClosed">
                    <div v-if="match.mainMarketEventResult.extraInfo && match.mainMarketEventResult.extraInfo.round > 0 || !liveStatus.onLive" class="match-info-status">
                        <div v-if="liveStatus.onLive && match.mainMarketEventResult.extraInfo" class="match-round">
                            {{ i18n.t('mtMatchDisplay_roundNumber', { no: match.mainMarketEventResult.extraInfo.round }) }}
                        </div>
                        <div v-else class="match-date">
                            {{ eventDate }}
                            <time v-if="match.showTime">{{ formatShowTime(match.showTime, match.showTimeType) }}</time>
                        </div>
                    </div>
                </template>
                <div
                    class="match-info-team-name JARVIS_MatchCard_Img_TeamNameB"
                    v-bind:class="{
                        _favorite: match.isAwayFavorite,
                        _underdog: !match.isAwayFavorite,
                    }"
                >
                    {{ awayTeamName }}
                </div>
            </div>
        </div>
        <template v-if="match && !match.isClosed">
            <div v-if="!loaded" style="height: 8.5rem; padding-top: 4rem; background: #fbfbfb;">
                <Spinner />
            </div>
            <div
                v-else
                class="match-betArea"
                v-bind:class="{ _live: match.isLive }"
            >
                <div
                    class="match-betArea-content"
                    v-on:dblclick.prevent.stop
                >
                    <div class="match-betArea-type-title">
                        <p
                            class="team-name"
                            v-bind:class="{
                                _favorite: match.isHomeFavorite,
                                _underdog: !match.isHomeFavorite,
                            }"
                        >
                            {{ homeTeamName }}
                        </p>
                        <p
                            class="team-name"
                            v-bind:class="{
                                _favorite: match.isAwayFavorite,
                                _underdog: !match.isAwayFavorite,
                            }"
                        >
                            {{ awayTeamName }}
                        </p>
                    </div>
                    <div v-if="oddsInfo" class="match-oddArea">
                        <Odds
                            v-for="priceOption in oddsInfo.priceOption"
                            v-bind:key="priceOption.option"
                            v-bind:oddsInfo="oddsInfo"
                            v-bind:priceOption="priceOption"
                            v-bind:matchInfo="match"
                        />
                    </div>
                    <div v-else class="match-oddArea-onGoing">
                        <div class="odd" />
                        <div class="odd" />
                        <p>
                            {{ i18n.t('mtMatchDisplay_fightingOnGoing') }}
                        </p>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import type { PropType } from 'vue';
    import { computed, defineComponent, onBeforeUnmount, onMounted, ref, toRefs } from 'vue';
    import { useRoute } from 'vue-router';
    import Spinner from '@/components/common/Spinner.vue';
    import Odds from '@/components/match/Odds.vue';
    import type { IMatch } from '@/interface/IMatch';
    import { MarketPage, MarketType, RoutePage } from '@/interface/enum';
    import { getTranslationBySite } from '@/libraries/helpers/i18nHelper';
    import { formatShowTime } from '@/libraries/helpers/showTimeHelper';
    import { getTranslated } from '@/libraries/helpers/translationHelper';
    import { i18n } from '@/libraries/i18n';
    import { useOdds } from '@/libraries/oddsApi/composables/useOdds';
    import { useRootStore } from '@/stores/rootStore';
    import SvgIcon from '@/svg/SvgIcon.vue';
    import { EnumPresetFilterType } from '@/types';

    export default defineComponent({
        name: 'MtMatchDisplay',
        components: {
            Odds,
            Spinner,
            SvgIcon,
        },
        props: {
            match: {
                type: Object as PropType<IMatch>,
                default: () => ({}),
            },
        },
        setup(props) {
            const ticks = ref(0);
            const isRefreshing = ref(false);

            const { match } = toRefs(props);

            const { isMixParlayMode } = storeToRefs(useRootStore());
            const isLive = computed(() => match.value.isLive);
            const presetFilter = computed(() => (isLive.value ? EnumPresetFilterType.Live : EnumPresetFilterType.NonLive));

            const { oddsList, loaded, refetch } = useOdds({
                eventId: match.value.eventId,
                isMixParlayMode,
                presetFilter,
                isLive,
                isEnabled: true,
                sportType: match.value.sportType,
                leagueSize: match.value.leagueSize,
            });

            const oddsInfo = computed(() => {
                if (!oddsList.value) return null;
                const displayOdds = oddsList.value.filter(odd => odd.marketType === MarketType.Handicap);
                return displayOdds[0] || null;
            });

            const refreshDuration = computed(() => (match.value.isLive ? 30 : 60));

            const isOutright = computed(() => match.value.marketPage === MarketPage.Outright);

            const liveStatus = computed(() => ({
                tvLive: match.value.isTvLive,
                onLive: match.value.isLive || (isOutright.value && match.value.isLive),
                isToday: match.value.marketPage === MarketPage.Today,
            }));

            const route = useRoute();
            const isInMatchDetail = computed(() => route.name === RoutePage.MatchDetail);

            const eventDate = computed(() => {
                if (match.value.eventDate) {
                    return match.value.eventDate;
                }
                return '';
            });

            const leagueName = computed(() => getTranslated(match.value.leagueName));

            const homeTeamName = computed(() => getTranslated(match.value.homeTeam?.teamName));

            const awayTeamName = computed(() => getTranslated(match.value.awayTeam?.teamName));

            window.eventEmitter.on('tick', tick);
            window.eventEmitter.on('triggerRefreshAnimation', onTriggerRefreshAnimation);

            onMounted(() => {
                window.eventEmitter.emit('refreshMatch', match.value.eventId, true);
            });

            onBeforeUnmount(() => {
                window.eventEmitter.off('tick', tick);
                window.eventEmitter.off('triggerRefreshAnimation', onTriggerRefreshAnimation);
            });

            function tick() {
                ticks.value++;
                if (ticks.value >= refreshDuration.value) {
                    refresh(true);
                }
            }

            function refresh(force: boolean) {
                if (isRefreshing.value) return;

                ticks.value = 0;

                if (force) {
                    realRefresh();
                } else {
                    fakeRefresh();
                }
            }

            function fakeRefresh() {
                refreshAnimation();
            }

            function realRefresh() {
                refreshAnimation();
                refetch();
            }

            function onTriggerRefreshAnimation(eventId: number) {
                if (eventId === match.value.eventId) {
                    refreshAnimation();
                }
            }

            function refreshAnimation() {
                isRefreshing.value = true;
                setTimeout(() => {
                    isRefreshing.value = false;
                }, 1000);
            }

            return {
                oddsInfo,
                i18n,
                liveStatus,
                isRefreshing,
                loaded,
                eventDate,
                isInMatchDetail,
                refresh,
                getTranslationBySite,
                formatShowTime,
                leagueName,
                homeTeamName,
                awayTeamName,
            };
        },
    });
</script>

<style lang="scss" scoped>
@import "@/scss/components/mtMatchDisplay";
</style>
